<template>
  <div>
    <button-back
      class="bonuses-page__back-btn"
      :title="getContent(layoutData, defaultLocaleLayoutData, 'profileSidebar.menuTitle.bonuses')"
      @click="router.back()"
    />

    <div class="bonuses-page">
      <div class="bonuses-page__container">
        <client-only>
          <div v-if="profile" class="bonuses-page__banner">
            <div class="bonuses-page__banner-content">
              <div class="bonuses-page__banner-heading">
                <span class="bonuses-page__banner-heading-title">
                  {{ getContent(bonusesContent, defaultLocaleBonusesContent, 'title') }}
                </span>
                <span class="bonuses-page__banner-heading-subtitle">
                  {{ getContent(bonusesContent, defaultLocaleBonusesContent, 'subtitle') }}
                </span>
              </div>
              <div class="bonuses-page__banner-info-group">
                <div class="bonuses-page__banner-info">
                  <div class="bonuses-page__banner-title">
                    {{ getContent(bonusesContent, defaultLocaleBonusesContent, 'freeSpins.title') }}
                  </div>
                  <div class="bonuses-page__banner-value">{{ freeSpinsValue }}</div>
                </div>

                <div class="bonuses-page__banner-info">
                  <div class="bonuses-page__banner-title">
                    {{ getContent(bonusesContent, defaultLocaleBonusesContent, 'cashBonuses.title') }}
                  </div>
                  <div class="bonuses-page__banner-value">
                    <atomic-icon id="usdt" />
                    {{ cashBonusValue }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="profile" class="bonuses-page__bonus-info">
            <tab-tabs ref="tabsRef" :items="bonusesTabs" @changeTab="changeTab" />

            <template v-if="currentInfoBonusTab === 'cashBonuses'">
              <bonus-active
                v-if="playerBonuses.length"
                bonusType="bonus"
                :content="bonusesContent"
                :defaultLocaleContent="defaultLocaleBonusesContent"
              />

              <atomic-custom-empty
                v-else
                :title="getContent(layoutData, defaultLocaleLayoutData, 'empty.noDataCashBonuses.title')"
                :description="getContent(layoutData, defaultLocaleLayoutData, 'empty.noDataCashBonuses.description')"
                :image="getContent(layoutData, defaultLocaleLayoutData, 'empty.noDataCashBonuses.img')"
              />
            </template>

            <template v-if="currentInfoBonusTab === 'freeSpins'">
              <bonus-active
                v-if="playerFreeSpins.length"
                bonusType="free-spin"
                :content="bonusesContent || defaultLocaleBonusesContent"
              />

              <atomic-custom-empty
                v-else
                :title="getContent(layoutData, defaultLocaleLayoutData, 'empty.noDataFeeSpinsBonuses.title')"
                :description="
                  getContent(layoutData, defaultLocaleLayoutData, 'empty.noDataFeeSpinsBonuses.description')
                "
                :image="getContent(layoutData, defaultLocaleLayoutData, 'empty.noDataFeeSpinsBonuses.img')"
              />
            </template>
          </div>
        </client-only>

        <welcome-package-intro
          class="bonuses-page_welcome-intro"
          :title="getContent(promotionsContent, defaultLocalePromotionsContent, 'title')"
          :description="getContent(promotionsContent, defaultLocalePromotionsContent, 'description')"
          :how-get-label="getContent(promotionsContent, defaultLocalePromotionsContent, 'howGet.label')"
          :how-get-steps="getContent(promotionsContent, defaultLocalePromotionsContent, 'howGet.steps', [])"
        />

        <welcome-package-card
          v-if="getContent(promotionsContent, defaultLocalePromotionsContent, 'welcome.items')?.length"
          :title="getContent(promotionsContent, defaultLocalePromotionsContent, 'welcome.label')"
          :cardItems="getContent(promotionsContent, defaultLocalePromotionsContent, 'welcome.items')"
        />

        <div
          v-if="getContent(promotionsContent, defaultLocalePromotionsContent, 'bonuses.items')?.length"
          class="bonuses-page__packages"
        >
          <span class="bonuses-page__packages--title">{{ promotionsContent?.bonuses.label }}</span>
          <div class="bonuses-page__packages-container">
            <div
              v-for="(bonus, index) in getContent(promotionsContent, defaultLocalePromotionsContent, 'bonuses.items')"
              :key="index"
              class="bonuses-page__packages--group"
            >
              <div :class="['bonuses-page__package--card', `bonuses-page__package--card-weekly`]">
                <div class="bonuses-page__package--card-weekly-img">
                  <atomic-image
                    class="bonuses-page__package--card-img"
                    :src="bonus.image"
                    :defaultImage="DEFAULT_IMG"
                  />
                </div>
                <div class="bonuses-page__package--card-right">
                  <div class="bonuses-page__package--card-info">
                    <span class="bonuses-page__package--card-heading-2">{{ bonus?.title }}</span>
                    <span class="bonuses-page__package--card-text-2">{{ bonus?.subtitle }}</span>
                  </div>
                  <div class="bonuses-page__package--card-buttons">
                    <div class="bonuses-page__package--card-button">
                      <span>{{ bonus.buttonLabel }}</span>
                    </div>
                    <div
                      class="bonuses-page__package--card-button bonuses-page__package--card-button-secondary-2"
                      @click="router.push(bonus?.link?.url ?? '/')"
                    >
                      <span>{{ bonus?.link?.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { DEFAULT_IMG } from '@skeleton/consts/staticPath';
  import type { IProfileBonuses, IWelcomeBonusesPage } from '~/types';

  interface IPageContent {
    currentLocaleData: Maybe<IWelcomeBonusesPage>;
    defaultLocaleData: Maybe<IWelcomeBonusesPage>;
  }

  interface IPageBonusesContent {
    currentLocaleData: Maybe<IProfileBonuses>;
    defaultLocaleData: Maybe<IProfileBonuses>;
  }

  const loadingStore = useLoadingStore();
  const { playerBonuses, playerFreeSpins } = storeToRefs(useBonusStore());
  const { profile } = storeToRefs(useProfileStore());
  const { layoutData, defaultLocaleLayoutData } = useGlobalStore();
  const { getPlayerBonuses, getPlayerFreeSpins } = useBonusStore();
  const { setPageMeta, getContent } = useProjectMethods();
  const router = useRouter();

  const promotionsContent = ref<Maybe<IWelcomeBonusesPage>>();
  const defaultLocalePromotionsContent = ref<Maybe<IWelcomeBonusesPage>>();

  const currentInfoBonusTab = ref('cashBonuses');
  const bonusesContent = ref<Maybe<IPageBonusesContent>>();
  const defaultLocaleBonusesContent = ref<Maybe<IPageBonusesContent>>();

  const bonusesTabs = computed(() => {
    return [
      {
        id: 'cashBonuses',
        title: getContent(bonusesContent.value, defaultLocaleBonusesContent.value, 'cashBonuses.title'),
      },
      {
        id: 'freeSpins',
        title: getContent(bonusesContent.value, defaultLocaleBonusesContent.value, 'freeSpins.title'),
      },
    ];
  });

  const cashBonusValue = computed(() => {
    if (!profile.value) return 0;

    const value =
      playerBonuses.value?.reduce((acc, item) => {
        return item.status === 2 ? acc + item.amount : acc;
      }, 0) || 0;

    return value?.toFixed(2);
  });

  const freeSpinsValue = computed(() => {
    if (!profile.value) return 0;

    return (
      playerFreeSpins.value?.reduce((acc, item) => {
        return acc + item.count;
      }, 0) || 0
    );
  });

  const setContentData = (contentData: Maybe<IPageContent>): void => {
    promotionsContent.value = contentData?.currentLocaleData;
    defaultLocalePromotionsContent.value = contentData?.defaultLocaleData;
  };

  const changeTab = (id: string) => {
    currentInfoBonusTab.value = id;
  };

  const { getContentData: getWelcomeContentData } = useContentLogic<IWelcomeBonusesPage>({
    contentKey: 'promotionsContent',
    contentRoute: ['pages', 'welcome-bonuses'],
    isPage: false,
  });

  const { data } = await useLazyAsyncData(getWelcomeContentData);

  if (data.value) setContentData(data.value);

  watch(data, () => {
    setContentData(data.value);
  });

  const getBonusesData = async () => {
    await getPlayerBonuses();
    await getPlayerFreeSpins();
  };

  const { getContentData: getBonusesContentData } = useContentLogic<IPageBonusesContent>({
    contentKey: 'profileBonusesContent',
    contentRoute: ['profile', 'bonuses'],
    isPage: false,
  });

  const { data: bonusesContentData } = await useLazyAsyncData(getBonusesContentData);

  const loading = computed(() => {
    return !data.value || !bonusesContentData.value;
  });

  watchEffect(() => {
    loadingStore.setLoadingState(loading.value);
  });

  watch(
    bonusesContentData,
    () => {
      if (bonusesContentData.value) {
        setPageMeta(bonusesContentData.value?.currentLocaleData?.pageMeta);
        bonusesContent.value = bonusesContentData.value?.currentLocaleData;
        defaultLocaleBonusesContent.value = bonusesContentData.value?.defaultLocaleData;
      }
    },
    { immediate: true }
  );

  onMounted(async () => {
    if (profile.value) {
      await getBonusesData();
    }
  });
</script>

<style src="~/assets/styles/pages/bonuses.scss" lang="scss" />
